import { useEffect, useState } from "react";
import { ExpandAltOutlined } from "@ant-design/icons";
import { Modal } from "antd";
import PageContainer from "../../../components/page-container";
import Button from "../../../components/button/button";
import Message from "../../../components/toast-message";
import { useAppInfo } from "../../../helpers/hooks/common-hook";
import {
	getPromptConfigInstructions,
	createPromptConfigInstructions,
	updatePromptConfigInstructions,
} from "../../../services/prompt-ai";
import { AGENT_NAVIGATION } from "../../agent-manage/consts/navigations";

const openai_provider_key = "openai";
const gemini_provider_key = "gemini";

const VOICES = {
	[openai_provider_key]: [
		{ value: 'alloy', label: 'Alloy' },
		{ value: 'ash', label: 'Ash' },
		{ value: 'ballad', label: 'Ballad' },
		{ value: 'cedar', label: 'Cedar' },
		{ value: 'coral', label: 'Coral' },
		{ value: 'echo', label: 'Echo' },
		{ value: 'marin', label: 'Marin' },
		{ value: 'sage', label: 'Sage' },
		{ value: 'shimmer', label: 'Shimmer' },
		{ value: 'verse', label: 'Verse' },
	],
	[gemini_provider_key]: [
		{ "value": "zephyr", "label": "Zephyr -- Bright", "style": "Bright" },
		{ "value": "puck", "label": "Puck -- Upbeat", "style": "Upbeat" },
		{ "value": "charon", "label": "Charon -- Informative", "style": "Informative" },
		{ "value": "kore", "label": "Kore -- Firm", "style": "Firm" },
		{ "value": "fenrir", "label": "Fenrir -- Excitable", "style": "Excitable" },
		{ "value": "leda", "label": "Leda -- Youthful", "style": "Youthful" },
		{ "value": "orus", "label": "Orus -- Firm", "style": "Firm" },
		{ "value": "aoede", "label": "Aoede -- Breezy", "style": "Breezy" },
		{ "value": "callirrhoe", "label": "Callirrhoe -- Easy-going", "style": "Easy-going" },
		{ "value": "autonoe", "label": "Autonoe -- Bright", "style": "Bright" },
		{ "value": "enceladus", "label": "Enceladus -- Breathy", "style": "Breathy" },
		{ "value": "iapetus", "label": "Iapetus -- Clear", "style": "Clear" },
		{ "value": "umbriel", "label": "Umbriel -- Easy-going", "style": "Easy-going" },
		{ "value": "algieba", "label": "Algieba -- Smooth", "style": "Smooth" },
		{ "value": "despina", "label": "Despina -- Smooth", "style": "Smooth" },
		{ "value": "erinome", "label": "Erinome -- Clear", "style": "Clear" },
		{ "value": "algenib", "label": "Algenib -- Gravelly", "style": "Gravelly" },
		{ "value": "rasalgethi", "label": "Rasalgethi -- Informative", "style": "Informative" },
		{ "value": "laomedeia", "label": "Laomedeia -- Upbeat", "style": "Upbeat" },
		{ "value": "achernar", "label": "Achernar -- Soft", "style": "Soft" },
		{ "value": "alnilam", "label": "Alnilam -- Firm", "style": "Firm" },
		{ "value": "schedar", "label": "Schedar -- Even", "style": "Even" },
		{ "value": "gacrux", "label": "Gacrux -- Mature", "style": "Mature" },
		{ "value": "pulcherrima", "label": "Pulcherrima -- Forward", "style": "Forward" },
		{ "value": "achird", "label": "Achird -- Friendly", "style": "Friendly" },
		{ "value": "zubenelgenubi", "label": "Zubenelgenubi -- Casual", "style": "Casual" },
		{ "value": "vindemiatrix", "label": "Vindemiatrix -- Gentle", "style": "Gentle" },
		{ "value": "sadachbia", "label": "Sadachbia -- Lively", "style": "Lively" },
		{ "value": "sadaltager", "label": "Sadaltager -- Knowledgeable", "style": "Knowledgeable" },
		{ "value": "sulafat", "label": "Sulafat -- Warm", "style": "Warm" }
	]
};

function DropdownField({ label, description, value, onChange, options, disabled }) {
	return (
		<div>
			<p className="font-semibold text-sm text-gray-800 dark:text-white mb-1">{label}</p>
			<p className="text-xs text-gray-500 mb-2">{description}</p>
			<select
				value={value}
				onChange={(e) => onChange(e.target.value)}
				disabled={disabled}
				className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-800 bg-white focus:outline-none focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:border-gray-600 dark:text-white disabled:opacity-50"
			>
				{options.map((opt) => (
					<option key={opt.value} value={opt.value}>{opt.label}</option>
				))}
			</select>
		</div>
	);
}

function PromptTextArea({ value, onChange, minRows = 12, disabled }) {
	return (
		<textarea
			value={value}
			onChange={(e) => onChange(e.target.value)}
			placeholder="You are a helpful assistant..."
			rows={minRows}
			disabled={disabled}
			className="w-full resize-none border-none outline-none focus:ring-0 focus:border-transparent text-sm text-gray-800 dark:text-white dark:bg-gray-800 placeholder-gray-400 disabled:opacity-50"
		/>
	);
}

export default function AgentPromptBased() {
	const { appId } = useAppInfo();

	const [prompt, setPrompt] = useState("");
	const [llm, setLlm] = useState(openai_provider_key);
	const [voice, setVoice] = useState(VOICES[openai_provider_key][0].value);
	const [isModalOpen, setIsModalOpen] = useState(false);

	// Track whether a record already exists (to know POST vs PUT)
	const [isExisting, setIsExisting] = useState(false);

	const [loadState, setLoadState] = useState({ processing: false, failed: false });
	const [saveState, setSaveState] = useState({ processing: false, success: false, failed: false });

	// --- Load ---
	async function fetchInstructions() {
		setLoadState({ processing: true, failed: false });
		const data = await getPromptConfigInstructions(appId);
		if (data) {
			setPrompt(data.globalPrompt || "");
			setLlm(data.voiceProvider || openai_provider_key);
			setVoice(data.voice || VOICES[data.voiceProvider || openai_provider_key][0].value);
			setIsExisting(true);
		}
		setLoadState({ processing: false, failed: !data && false }); // silent fail on 404
	}

	useEffect(() => {
		if (appId) {
			fetchInstructions();
		}
	}, [appId]);

	// --- LLM change resets voice ---
	const handleLlmChange = (value) => {
		setLlm(value);
		setVoice(VOICES[value][0].value);
	};

	// --- Clear ---
	const handleClear = () => {
		setPrompt("");
		setLlm(openai_provider_key);
		setVoice(VOICES[openai_provider_key][0].value);
		setSaveState({ processing: false, success: false, failed: false });
	};

	// --- Save (POST or PUT) ---
	const handleSave = async () => {
		setSaveState({ processing: true, success: false, failed: false });
		const payload = {
			globalPrompt: prompt,
			voiceProvider: llm,
			voice,
		};
		try {
			const { status } = isExisting
				? await updatePromptConfigInstructions(appId, payload)
				: await createPromptConfigInstructions(appId, payload);

			if (status === 200 || status === 201) {
				setIsExisting(true);
				setSaveState({ processing: false, success: true, failed: false });
			} else {
				setSaveState({ processing: false, success: false, failed: true });
			}
		} catch {
			setSaveState({ processing: false, success: false, failed: true });
		}
	};

	const isBusy = loadState.processing || saveState.processing;

	return (
		<PageContainer
			title="Agent"
			subtitle="Instruct your agent with a prompt and let it handle the rest"
			navigation={AGENT_NAVIGATION}
			headerAction={
				<div className="flex flex-row items-center gap-3">
					<Button text="Clear" color="default" onClick={handleClear} disabled={isBusy} />
					<Button text="Save" onClick={handleSave} loading={saveState.processing} disabled={isBusy} />
				</div>
			}
		>
			{saveState.success && (
				<div className="mb-4">
					<Message text="Settings saved successfully." type="success" />
				</div>
			)}
			{saveState.failed && (
				<div className="mb-4">
					<Message text="Failed to save settings. Please try again." type="failure" />
				</div>
			)}

			{loadState.processing ? (
				<div className="flex justify-center items-center py-16">
					<p className="text-sm text-gray-400">Loading…</p>
				</div>
			) : (
				<div className="flex flex-row gap-6 mt-6 px-1">
					{/* Left Panel: Prompt Editor */}
					<div className="flex-1">
						<div className="border border-gray-300 rounded-lg bg-white dark:bg-gray-800 dark:border-gray-700 p-4">
							<div className="flex justify-between items-center mb-2">
								<span className="font-semibold text-sm text-gray-800 dark:text-white">System prompt</span>
								<button
									type="button"
									onClick={() => setIsModalOpen(true)}
									title="Expand Editor"
									className="text-gray-500 hover:text-primary transition-colors p-1 rounded"
								>
									<ExpandAltOutlined />
								</button>
							</div>
							<PromptTextArea value={prompt} onChange={setPrompt} />
						</div>
					</div>

					{/* Right Panel: Settings */}
					<div className="w-64 flex flex-col gap-6">
						<DropdownField
							label="LLM"
							description="Select which provider and model to use for the LLM."
							value={llm}
							onChange={handleLlmChange}
							options={[
								{ value: 'openai', label: 'OpenAI' },
								{ value: 'gemini', label: 'Gemini' },
							]}
						/>

						<DropdownField
							label="Voices"
							description="Select the voice you want to use for the agent."
							value={voice}
							onChange={setVoice}
							options={VOICES[llm]}
						/>
					</div>
				</div>
			)}

			{/* Expanded Prompt Modal */}
			<Modal
				title={<span className="font-semibold text-gray-800">System prompt</span>}
				open={isModalOpen}
				onOk={() => setIsModalOpen(false)}
				onCancel={() => setIsModalOpen(false)}
				width={800}
				footer={
					<div className="flex justify-end">
						<Button text="Done" onClick={() => setIsModalOpen(false)} />
					</div>
				}
			>
				<div className="mt-3">
					<PromptTextArea value={prompt} onChange={setPrompt} minRows={20} />
				</div>
			</Modal>
		</PageContainer>
	);
}